import { useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { AmountQuestion } from 'src/suitability/engine/components/amount-question';
import { MultiSelectQuestion } from 'src/suitability/engine/components/multi-select-question';
import { RadioQuestion } from 'src/suitability/engine/components/radio-question';
import { TextQuestion } from 'src/suitability/engine/components/text-question';
import type { TestStepProps } from 'src/suitability/engine/types';
import {
  Q23_OTHER_ORIGIN_ORDER,
  type ConsilioAnswers,
  type ConsilioWealthOrigin,
} from './answers';
import { asksForIncomeBracket } from './completeness';
import {
  toMultiSelectOptions,
  toRadioOptions,
  useConsilioContent,
} from './content';

const toSelection = (origins: number[]): Record<string, boolean> =>
  Object.fromEntries(origins.map((order) => [String(order), true]));

const toOrigins = (selection: Record<string, boolean>): number[] =>
  Object.entries(selection)
    .filter(([, selected]) => selected)
    .map(([order]) => Number(order))
    .sort((a, b) => a - b);

export const StepAdditional = ({
  answers,
  onUpdate,
  context,
}: TestStepProps<ConsilioAnswers>) => {
  const content = useConsilioContent();
  const step = content.step('additional');
  const q23 = content.question('q23');
  const q24 = content.question('q24');
  const wealthOrigin = answers.q23 ?? {};
  const origins = wealthOrigin.origins ?? [];
  const asksIncome = asksForIncomeBracket(context);
  const otherOrigin = q23.options?.find(
    (option) => option.order === Q23_OTHER_ORIGIN_ORDER,
  );

  useEffect(() => {
    if (!asksIncome && answers.q24 !== undefined) onUpdate({ q24: undefined });
  }, [asksIncome, answers.q24, onUpdate]);

  const updateWealthOrigin = (patch: ConsilioWealthOrigin) =>
    onUpdate({ q23: { ...wealthOrigin, ...patch } });

  const handleOrigins = (selection: Record<string, boolean>) => {
    const next = toOrigins(selection);

    updateWealthOrigin({
      origins: next,
      otherOrigin: next.includes(Q23_OTHER_ORIGIN_ORDER)
        ? wealthOrigin.otherOrigin
        : undefined,
    });
  };

  return (
    <Box>
      <Typography variant="textXl" sx={{ fontWeight: 700, mb: 1 }}>
        {step.title}
      </Typography>
      {step.intro && (
        <Typography
          variant="textSm"
          sx={{ color: 'text.secondary', mb: 3, display: 'block' }}
        >
          {step.intro}
        </Typography>
      )}

      <Typography
        variant="textMd"
        component="h3"
        sx={{ fontWeight: 600, mb: 1.5, display: 'block' }}
      >
        {q23.prompt}
      </Typography>
      {q23.wealth && (
        <AmountQuestion
          question={q23.wealth.label}
          suffix={q23.wealth.suffix}
          value={wealthOrigin.estimatedTotalWealth}
          onChange={(estimatedTotalWealth) =>
            updateWealthOrigin({ estimatedTotalWealth })
          }
        />
      )}
      <Box sx={{ mb: 3 }}>
        <MultiSelectQuestion
          options={toMultiSelectOptions(q23)}
          value={toSelection(origins)}
          onChange={handleOrigins}
        />
      </Box>
      {otherOrigin && origins.includes(Q23_OTHER_ORIGIN_ORDER) && (
        <TextQuestion
          question={otherOrigin.label}
          value={wealthOrigin.otherOrigin}
          onChange={(text) => updateWealthOrigin({ otherOrigin: text })}
          required
        />
      )}

      {asksIncome && (
        <RadioQuestion
          question={q24.prompt}
          options={toRadioOptions('q24', q24)}
          value={answers.q24 === undefined ? undefined : String(answers.q24)}
          onChange={(value) => onUpdate({ q24: Number(value) })}
          required
        />
      )}
    </Box>
  );
};
